import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { enqueueSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import Grid from '@material-ui/core/Grid';
import IconButton from '@material-ui/core/IconButton';
import { useTheme } from '@material-ui/core/styles';
import { mdiClose, mdiCreation } from '@mdi/js';
import Icon from '@mdi/react';
import Button from 'components/Button/Button';
import { BankBottomSheet } from 'scenes/ProjectBank/components/BankBottomSheet/BankBottomSheet';
import { selectSelectedFilepermissionMediaTags } from 'store/mediaTags/selectors/mediaTags.selectors';
import { removeTagLinkFromFile } from 'store/mediaTags/thunks/removeTagLinkFromFile.thunk';
import { isAiTag } from '../ImageMediaTags/MediaTagChip';
import {
  VIEWER_TAG_SHEET_ID,
  getMediaTagLabel,
  getViewerSheetZIndex,
} from './mobileMediaTags';

const TITLE_ID = `${VIEWER_TAG_SHEET_ID}-title`;

/**
 * What the sheet holds, mounted while it is open (and while it slides out).
 * It shows the tag it last found among the photo's tags: once removed, the
 * tag is gone from the store while the sheet slides out, and the sheet still
 * names it.
 */
function TagSheetContent({ tagLinkId, onClose }) {
  const theme = useTheme();
  const { t } = useTranslation('common');
  const dispatch = useDispatch();
  const tags = useSelector(selectSelectedFilepermissionMediaTags);

  const found = (tags || []).find((tag) => tag.id === tagLinkId);
  const shownRef = useRef(found);
  if (found) shownRef.current = found;
  const tag = shownRef.current;
  const isAi = Boolean(tag) && isAiTag(tag);

  const [isRemoving, setIsRemoving] = useState(false);
  const mountedRef = useRef(true);
  useEffect(
    () => () => {
      mountedRef.current = false;
    },
    []
  );

  const handleRemove = async () => {
    setIsRemoving(true);
    const result = await dispatch(removeTagLinkFromFile(tag.id));
    if (result.error) {
      // The slice has no rejected case for the removal: nothing else says so.
      enqueueSnackbar(t('gallery.remove_tag_failed'), { variant: 'error' });
      if (mountedRef.current) setIsRemoving(false);
    } else if (mountedRef.current) {
      // Stays disabled while the sheet slides out.
      onClose();
    }
  };

  return (
    <>
      <Grid
        container
        alignItems="center"
        wrap="nowrap"
        style={{
          flexShrink: 0,
          gap: theme.spacing(1),
          padding: theme.spacing(1, 1, 0, 2),
        }}
      >
        {isAi ? (
          <Grid item style={{ display: 'flex', flexShrink: 0 }}>
            <Icon path={mdiCreation} size="20px" />
          </Grid>
        ) : null}
        <Grid item xs zeroMinWidth>
          {/* The whole name, which the chip may have cut; a long one wraps. */}
          <h2
            id={TITLE_ID}
            style={{
              margin: 0,
              fontFamily: theme.typography.fontPanton.fontFamily,
              fontSize: '1rem',
              fontWeight: 'normal',
              lineHeight: 1.3,
              color: theme.palette.text.primary,
              overflowWrap: 'break-word',
              wordBreak: 'break-word',
            }}
          >
            {tag ? getMediaTagLabel(tag) : ''}
          </h2>
        </Grid>
        <Grid item style={{ flexShrink: 0 }}>
          <IconButton
            aria-label={t('common.close')}
            title={t('common.close')}
            data-viewer-tag-sheet-close="true"
            onClick={onClose}
          >
            <Icon path={mdiClose} size={0.8} />
          </IconButton>
        </Grid>
      </Grid>
      {isAi ? (
        <p
          data-viewer-ai-tag-note="true"
          style={{
            margin: 0,
            padding: theme.spacing(0, 2),
            fontFamily: theme.typography.fontPanton.fontFamily,
            fontSize: '0.8rem',
            lineHeight: 1.3,
            color: theme.palette.text.secondary,
          }}
        >
          {t('gallery.ai_tag')}
        </p>
      ) : null}
      <div
        style={{
          flexShrink: 0,
          padding: theme.spacing(2, 2, 1),
          paddingBottom: `calc(${theme.spacing(
            1
          )}px + env(safe-area-inset-bottom, 0px))`,
        }}
      >
        <Button
          rounded
          outline
          color="danger"
          fullWidth
          disabled={!tag || isRemoving}
          data-viewer-remove-tag="true"
          onClick={handleRemove}
        >
          {t('gallery.remove_tag')}
        </Button>
      </div>
    </>
  );
}

TagSheetContent.propTypes = {
  tagLinkId: PropTypes.string,
  onClose: PropTypes.func.isRequired,
};

/**
 * A tag of the photo in the viewer on a phone-sized screen, opened by a tap
 * on its chip in the tag row (MobileMediaTagRow): the whole name, which the
 * chip may have cut, a note when the AI tagger added it, and "Poista
 * tunniste", which takes the tag off the photo.
 *
 * The bank's bottom sheet (BankBottomSheet), over the viewer, which is a
 * dialog (getViewerSheetZIndex), and named by the tag. It closes by a swipe
 * down, a tap above it, Escape or its X, and once the tag is removed; a
 * removal that fails keeps it open and says so in a snackbar. What it shows
 * is kept while it slides out.
 * @param {Object} props
 * @param {boolean} props.open
 * @param {string} [props.tagLinkId] id of the photo's tag link it shows
 * @param {Function} props.onClose
 */
export function MobileMediaTagSheet({ open, tagLinkId, onClose }) {
  const theme = useTheme();

  return (
    <BankBottomSheet
      id={VIEWER_TAG_SHEET_ID}
      open={open}
      onClose={onClose}
      labelledBy={TITLE_ID}
      surface="base"
      zIndex={getViewerSheetZIndex(theme)}
      data-viewer-tag-sheet="true"
    >
      <TagSheetContent tagLinkId={tagLinkId} onClose={onClose} />
    </BankBottomSheet>
  );
}

MobileMediaTagSheet.propTypes = {
  open: PropTypes.bool.isRequired,
  /** Id of the photo's tag link the sheet shows. */
  tagLinkId: PropTypes.string,
  onClose: PropTypes.func.isRequired,
};

export default MobileMediaTagSheet;
